import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { UserFriendlyError } from '../../../model/Error'
import { useCallback, useMemo } from 'react'
import { Filters } from '../../../model/Filters'
import { singleItemPageLimit, startingPage } from '../../../constants'
import { getPatients } from '../../../data/patients'
import { getNextPageParam } from '../../../misc/query.utilities'
import { usePaginationState, useFlatPaginatedData } from '../../utils/query'
import { patientsKeys } from './patients.keys'

interface GetInfinitePatientParams {
  enabled?: boolean
  filters?: Filters
  limit?: number
}

export const useGetInfinitePatient = ({
  enabled = true,
  filters,
  limit = singleItemPageLimit,
}: GetInfinitePatientParams) => {
  const queryKey = useMemo(() => patientsKeys.list(filters), [filters])

  const queryReturn = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam, signal }) => {
      const { data, ok } = await getPatients(pageParam, filters, signal, limit)
      if (!data || !ok) {
        throw new UserFriendlyError(
          'error',
          `Impossible de récupérer la liste des patients`,
          `Une erreur technique s'est produite`,
        )
      }
      return data
    },
    initialPageParam: startingPage,
    getNextPageParam,
    enabled,
  })

  const queryClient = useQueryClient()
  const patientList = useFlatPaginatedData(queryReturn.data)
  const paginationState = usePaginationState(queryReturn.data)

  const cancelPendingQuery = useCallback(() => {
    queryClient.cancelQueries({ queryKey })
  }, [queryClient, queryKey])

  return { query: queryReturn, patientList, paginationState, cancelPendingQuery }
}
